/* eslint-disable react/prop-types */
/**
 * PaymentsTab — payments attached to the deal or one of its well_tracts.
 *
 * Scope picker (radio): [Deal] | [Well] | [Well Tract]
 *   - Deal (default): parent_table='deal' + parent_id=dealId. Editable.
 *   - Well: a READ-ONLY roll-up of every payment across ALL tracts in the
 *     selected well (UAT #13). Payments never attach to a well directly — only
 *     to a deal or a specific well_tract — so there is nothing to create/edit
 *     here; we fetch each tract's payments and combine them, and surface a
 *     "Well Tract" column so each row is attributable.
 *   - Well Tract: a cascading [Well -> Well Tract] picker drives the active
 *     (parent_table='well_tract', parent_id=<tract.id>). Editable, one tract.
 *
 * Payment-type ↔ parent_table mapping (not DB-enforced, surfaced as a hint):
 *   - BONUS, PROMOTE                        → parent=deal
 *   - DHC COSTS, COMPLETION COSTS, CWC COSTS → parent=well_tract
 *
 * Cell-renderer: color-coded Tag for payment_type so reviewers can
 * scan a long list by type at a glance.
 */
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Card,
  Radio,
  Select,
  Space,
  Tag,
  Typography,
  notification,
} from "antd";

import { WigMetadataCrudPanel } from "@warwick-investment-group/wig-react-library";

import { AppContext } from "../../../AppContextProvider";
import usePermissions from "../../../hooks/usePermissions";
import dealsService from "../../../services/dealsService";
import { apiErrorText } from "../../../utils/apiError";
import { numericCellRenderers } from "../../../utils/metadataV2Columns";
import { stripReadOnlyForUpdate } from "../../../utils/entityUpdateBody";

const { Text } = Typography;

// View scopes. 'deal' / 'well_tract' map 1:1 to the backend polymorphic
// parent_table (editable); 'well' is a read-only roll-up across all of a
// well's tracts (UAT #13) — NOT a real parent_table.
const SCOPE_OPTIONS = [
  { value: "deal", label: "Deal" },
  { value: "well", label: "Well" },
  { value: "well_tract", label: "Well Tract" },
];

// payment_type → AntD Tag color. Picked to read distinctly in both light
// and dark themes; greens for revenue-ish, blues/reds for costs.
const PAYMENT_TYPE_COLORS = {
  BONUS: "green",
  PROMOTE: "lime",
  "DHC COSTS": "geekblue",
  "COMPLETION COSTS": "purple",
  "CWC COSTS": "magenta",
};

// `payment_type` and `method_of_delivery` are now managed pick lists: the
// backend inlines their active values into `/metadata` as an `options` array
// (resolved from the `pick_lists` table), so we render whatever the metadata
// carries instead of a hardcoded enum. See the Admin → Pick Lists page.

// Hide parent fields — the active scope picker drives these. (The Well
// roll-up re-adds parent_id as a read-only "Well Tract" column below.)
const HIDDEN_FIELDS = new Set(["parent_table", "parent_id"]);

// A payments row carries two lifecycles: somebody ASKS for a check to be cut,
// and whoever cuts it RECORDS what happened. These are the second half.
// BD.Landman is the one role left holding only the first — it never sees these
// on the Add form. (BD.BrokerAgent did too until UAT #61; the brokerage cuts
// the check, so it now writes these columns.) The mirror of this set is
// PAYMENT_REQUEST_COLUMNS in de-hub's permissions.py, and POST /payments 403s
// naming the column if one is sent anyway.
const FULFILLMENT_FIELDS = new Set([
  "check_number",
  "date_paid",
  "method_of_delivery",
  "delivery_tracking_nbr",
  "date_pmt_recd_from_weg",
  "check_sent_to_field_date",
  "paid_override",
]);

// Mirrors WigMetadataCrudPanel's own default form filter so overriding it
// doesn't re-expose the audit columns it hides.
const PANEL_SERVER_MANAGED = new Set([
  "id",
  "created_at",
  "created_by",
  "updated_at",
  "updated_by",
]);

// The requester's Add form: everything the panel would normally show, minus
// the fulfillment columns. They stay visible as GRID columns — a broker needs
// to see whether their request has been paid, they just can't assert it.
const requesterFormFieldFilter = (row) =>
  !row.hide &&
  !PANEL_SERVER_MANAGED.has(row.field) &&
  !FULFILLMENT_FIELDS.has(row.field);

const wellLabel = (w) => {
  if (!w) return "";
  const name = w.well_name || `Well #${w.id}`;
  const api = w.api_10 ? ` — API ${w.api_10}` : "";
  return `${name}${api}`;
};
const tractLabel = (t) => {
  if (!t) return "";
  const loc = t.location_type ? `${t.location_type} ` : "";
  return `${loc}Tract #${t.id}`;
};

const PaymentsTab = ({ dealId }) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  const { can } = usePermissions();

  // Scope state
  const [scope, setScope] = useState("deal");
  const [wells, setWells] = useState([]);
  const [selectedWellId, setSelectedWellId] = useState(null);
  const [tracts, setTracts] = useState([]);
  const [selectedTractId, setSelectedTractId] = useState(null);

  // Grid state
  const [rows, setRows] = useState([]);
  const [metadata, setMetadata] = useState([]);
  const [entityOptions, setEntityOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState(null);

  // The Well scope is a read-only roll-up; Deal / Well Tract are writable.
  // Role gates on top of that, mirroring de-hub's split on this table:
  //   payment.recordCheck -> [BD.Accounting, BD.BrokerAgent]: unrestricted on
  //     the columns — create, edit, and every field including fulfillment.
  //     BD.BrokerAgent joined per UAT #61: the brokerage cuts the check, so it
  //     files the check number and delivery details itself. BD.LandTech is
  //     deliberately absent (UAT: a land tech may not enter a payment), so it
  //     falls through to the read-only case — grid only, no handlers.
  //   payment.delete -> [BD.Accounting]: deleting is NOT implied by editing.
  //     A payment is a polymorphic parent; removing it takes its remarks and
  //     attachments with it.
  //   payment.request -> [BD.BrokerAgent, BD.Landman]: submit a request only.
  //     For BD.Landman that is the whole of it — add-only (absent from
  //     PUT/DELETE /payments) and confined to the request columns via
  //     `requesterFormFieldFilter` above.
  // A wrong-scope panel stays read-only for everyone.
  const inWritableScope = scope !== "well";
  const mayRecord = can("payment.recordCheck");
  const mayRequest = can("payment.request");
  // Editing an existing payment row: the back office and the brokerage.
  const editable = inWritableScope && mayRecord;
  // Deleting one: the back office alone.
  const deletable = inWritableScope && can("payment.delete");
  // Creating is open to both — a requester files the ask, the back office
  // files a completed record.
  const creatable = inWritableScope && (mayRecord || mayRequest);
  const wellPickerVisible = scope === "well" || scope === "well_tract";

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  // Active (parent_table, parent_id) used by the grid + create/update body.
  // The Well roll-up has no single parent, so it is null there.
  const activeParentId = useMemo(() => {
    if (scope === "deal") return dealId;
    if (scope === "well_tract") return selectedTractId;
    return null;
  }, [scope, dealId, selectedTractId]);

  // Load metadata + the entities lookup once. (Neither depends on the active
  // scope.) The `paid_by_entity_id` field is a lookup select rendered as an
  // async LookupSelect, so entities feed the `lookupFetchers.entities` fetcher
  // below; the same list also resolves id -> name in the grid cell.
  useEffect(() => {
    const load = async () => {
      try {
        const { token: tk, userOid } = await getAuth();
        const [metaResp, entResp] = await Promise.all([
          dealsService.getMetadata({
            token: tk,
            userOid,
            tableName: "payments",
          }),
          dealsService.searchEntities({ token: tk, userOid }),
        ]);
        setMetadata(metaResp?.data?.data || []);
        const entRows = entResp?.data?.data || entResp?.data || [];
        setEntityOptions(
          Array.isArray(entRows)
            ? entRows.map((r) => ({
                value: r.id,
                label: r.name || r.display_name || `Entity ${r.id}`,
              }))
            : []
        );
      } catch (err) {
        console.error("PaymentsTab metadata load failed:", err);
        setErrMsg(apiErrorText(err));
      }
    };
    load();
  }, [getAuth]);

  // Load wells for the deal — whenever a well-scoped view (Well or Well Tract)
  // is active.
  useEffect(() => {
    if (!wellPickerVisible || !dealId) return;
    const load = async () => {
      try {
        const { token: tk, userOid } = await getAuth();
        const resp = await dealsService.listWells({
          token: tk,
          userOid,
          dealId,
        });
        const wRows = resp?.data?.data || [];
        setWells(wRows);
        setSelectedWellId((prev) => {
          if (prev && wRows.some((w) => w.id === prev)) return prev;
          return wRows[0]?.id ?? null;
        });
      } catch (err) {
        console.error("PaymentsTab wells load failed:", err);
        setErrMsg(apiErrorText(err));
      }
    };
    load();
  }, [wellPickerVisible, dealId, getAuth]);

  // Load tracts for the selected well (used both as the Well Tract picker's
  // options AND as the fan-out list for the Well roll-up).
  useEffect(() => {
    if (!wellPickerVisible || !selectedWellId) {
      setTracts([]);
      return;
    }
    const load = async () => {
      try {
        const { token: tk, userOid } = await getAuth();
        const resp = await dealsService.listWellTracts({
          token: tk,
          userOid,
          wellId: selectedWellId,
        });
        const tRows = resp?.data?.data || [];
        setTracts(tRows);
        setSelectedTractId((prev) => {
          if (prev && tRows.some((t) => t.id === prev)) return prev;
          return tRows[0]?.id ?? null;
        });
      } catch (err) {
        console.error("PaymentsTab tracts load failed:", err);
        setErrMsg(apiErrorText(err));
      }
    };
    load();
  }, [wellPickerVisible, selectedWellId, getAuth]);

  // Load payments for the active scope.
  //  - deal / well_tract: a single listPayments(parent_table, parent_id).
  //  - well: fan out one listPayments per tract and combine (payments attach
  //    to a tract, not a well, so there is no single-call well endpoint).
  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setErrMsg(null);
      const { token: tk, userOid } = await getAuth();

      if (scope === "well") {
        if (!selectedWellId || tracts.length === 0) {
          setRows([]);
          return;
        }
        const perTract = await Promise.all(
          tracts.map((t) =>
            dealsService
              .listPayments({
                token: tk,
                userOid,
                parentTable: "well_tract",
                parentId: t.id,
              })
              .then((r) => r?.data?.data || [])
              .catch(() => [])
          )
        );
        setRows(perTract.flat());
        return;
      }

      const parentId = scope === "deal" ? dealId : selectedTractId;
      if (!parentId) {
        setRows([]);
        return;
      }
      const resp = await dealsService.listPayments({
        token: tk,
        userOid,
        parentTable: scope,
        parentId,
      });
      setRows(resp?.data?.data || []);
    } catch (err) {
      console.error("PaymentsTab payments load failed:", err);
      setErrMsg(apiErrorText(err));
    } finally {
      setLoading(false);
    }
  }, [scope, dealId, selectedTractId, selectedWellId, tracts, getAuth]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Shape the metadata for the panel. Parent fields are hidden; the Well
  // roll-up re-adds parent_id as a read-only "Well Tract" column so each
  // combined row is attributable to its tract.
  const filteredMetadata = useMemo(() => {
    const base = (metadata || []).filter((m) => !HIDDEN_FIELDS.has(m.field));
    if (scope !== "well") return base;
    const parentIdMeta = (metadata || []).find((m) => m.field === "parent_id");
    const tractCol = {
      ...(parentIdMeta || { field: "parent_id" }),
      field: "parent_id",
      label: "Well Tract",
      hide: false,
      editable: false,
      input_type: "text",
      data_type: "Text",
      distinct_values_lookup: null,
    };
    return [tractCol, ...base];
  }, [metadata, scope]);

  // The `entities` async fetcher the Paid By LookupSelect needs (UAT #21k),
  // plus an id->name map to resolve the Paid By grid cell.
  const lookupFetchers = useMemo(
    () => ({
      entities: async (query) => {
        const { token: tk, userOid } = await getAuth();
        const resp = await dealsService.searchEntities({ token: tk, userOid, query });
        const rows = resp?.data?.data || resp?.data || [];
        return Array.isArray(rows)
          ? rows.map((r) => ({
              id: r.id,
              label: r.name || r.display_name || `Entity ${r.id}`,
            }))
          : [];
      },
    }),
    [getAuth]
  );

  const entityNameById = useMemo(() => {
    const map = {};
    for (const o of entityOptions) map[o.value] = o.label;
    return map;
  }, [entityOptions]);

  // Resolve a payment's parent_id -> tract label for the Well roll-up column.
  const tractLabelById = useMemo(() => {
    const map = {};
    for (const t of tracts) map[t.id] = tractLabel(t);
    return map;
  }, [tracts]);

  const cellRenderers = useMemo(
    () => ({
      // Money/interest/acreage formatting (payment_amount as currency).
      ...numericCellRenderers(filteredMetadata),
      payment_type: (params) => {
        if (!params?.value) return null;
        const color = PAYMENT_TYPE_COLORS[params.value] || "default";
        return <Tag color={color}>{params.value}</Tag>;
      },
      // Resolve the Paid By id -> entity name in the grid.
      paid_by_entity_id: (params) =>
        params?.value == null || params.value === ""
          ? ""
          : entityNameById[params.value] ?? params.value,
      // Well roll-up only: show which tract each payment belongs to. Inert in
      // the other scopes (the parent_id column isn't rendered there).
      parent_id: (params) =>
        params?.value == null || params.value === ""
          ? ""
          : tractLabelById[params.value] ?? `Tract #${params.value}`,
    }),
    [entityNameById, tractLabelById, filteredMetadata]
  );

  const surfaceParentError = (err) => {
    const msg = apiErrorText(err, { fallback: "Operation failed" });
    notification.error({ message: "Payment save failed", description: msg });
  };

  const onCreate = async (body) => {
    try {
      const { token: tk, userOid } = await getAuth();
      // A requester's form never renders the fulfillment columns, but strip
      // them from the body too — the server 403s on them, so a stray value
      // would surface as a confusing error rather than being ignored.
      const submitted = mayRecord
        ? body
        : Object.fromEntries(
            Object.entries(body || {}).filter(
              ([field]) => !FULFILLMENT_FIELDS.has(field)
            )
          );
      const resp = await dealsService.createPayment({
        token: tk,
        userOid,
        body: {
          ...submitted,
          parent_table: scope,
          parent_id: activeParentId,
        },
      });
      await refresh();
      return resp;
    } catch (err) {
      surfaceParentError(err);
      throw err;
    }
  };

  const onUpdate = async (id, body) => {
    try {
      const { token: tk, userOid } = await getAuth();
      const resp = await dealsService.updatePayment({
        token: tk,
        userOid,
        id,
        // Payments are polymorphic: parent_table/parent_id ARE updatable, so
        // keep them; strip the server-managed/read-only fields the row carries.
        body: {
          ...stripReadOnlyForUpdate(body),
          parent_table: scope,
          parent_id: activeParentId,
        },
      });
      await refresh();
      return resp;
    } catch (err) {
      surfaceParentError(err);
      throw err;
    }
  };

  const onDelete = async (id) => {
    const { token: tk, userOid } = await getAuth();
    await dealsService.deletePayment({ token: tk, userOid, id });
    await refresh();
  };

  const wellOptions = useMemo(
    () => (wells || []).map((w) => ({ value: w.id, label: wellLabel(w) })),
    [wells]
  );
  const tractOptions = useMemo(
    () => (tracts || []).map((t) => ({ value: t.id, label: tractLabel(t) })),
    [tracts]
  );

  const scopeReady =
    scope === "deal"
      ? Boolean(dealId)
      : scope === "well"
        ? Boolean(selectedWellId)
        : Boolean(selectedTractId);

  const headerLabel =
    scope === "deal"
      ? `Deal payments (${rows.length})`
      : scope === "well"
        ? selectedWellId
          ? `Well payments (${rows.length}) — ${wellLabel(
              wells.find((w) => w.id === selectedWellId)
            )} · all tracts`
          : "Pick a well to view payments"
        : selectedTractId
          ? `Well-tract payments (${rows.length}) — ${tractLabel(
              tracts.find((t) => t.id === selectedTractId)
            )}`
          : "Pick a well + tract to view payments";

  const selectPrompt =
    scope === "deal" ? "deal" : scope === "well" ? "well" : "well + tract";

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {errMsg && (
        <Alert
          type="error"
          showIcon
          message="Failed to load payments"
          description={errMsg}
        />
      )}

      <Card size="small" title="Scope">
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Space align="center" wrap>
            <Text strong>Show payments for:</Text>
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={scope}
              onChange={(e) => {
                setScope(e.target.value);
                setErrMsg(null);
              }}
              options={SCOPE_OPTIONS}
            />
          </Space>
          {wellPickerVisible && (
            <Space wrap align="center">
              <Text>Well:</Text>
              <Select
                value={selectedWellId}
                options={wellOptions}
                onChange={(val) => {
                  setSelectedWellId(val);
                  setSelectedTractId(null);
                }}
                placeholder="Pick a well"
                style={{ minWidth: 280 }}
                disabled={!wells.length}
                showSearch
                optionFilterProp="label"
                allowClear
              />
              {scope === "well_tract" && (
                <>
                  <Text>Tract:</Text>
                  <Select
                    value={selectedTractId}
                    options={tractOptions}
                    onChange={setSelectedTractId}
                    placeholder="Pick a tract"
                    style={{ minWidth: 220 }}
                    disabled={!tracts.length}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                  />
                </>
              )}
            </Space>
          )}
          {scope === "well" ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Read-only roll-up of every payment across all tracts in the
              selected well. Add or edit payments from the Deal or Well Tract
              scope.
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Hint: BONUS / PROMOTE typically attach to a Deal; DHC COSTS /
              COMPLETION COSTS / CWC COSTS typically attach to a Well Tract.
            </Text>
          )}
        </Space>
      </Card>

      <Card size="small" title={headerLabel}>
        {!scopeReady ? (
          <Text type="secondary">Select a {selectPrompt} to view payments.</Text>
        ) : (
          <WigMetadataCrudPanel
            title="Payments"
            rows={rows}
            metadata={filteredMetadata}
            loading={loading}
            allowView
            disableNonEditable
            onCreate={creatable ? onCreate : undefined}
            onUpdate={editable ? onUpdate : undefined}
            onDelete={deletable ? onDelete : undefined}
            lookupFetchers={lookupFetchers}
            cellRenderers={cellRenderers}
            // A requester's Add form drops the fulfillment columns. The back
            // office gets the panel's default filter (all fields).
            formFieldFilter={
              mayRecord ? undefined : requesterFormFieldFilter
            }
            newRowDefaults={
              creatable
                ? { parent_table: scope, parent_id: activeParentId }
                : undefined
            }
            emptyText={
              scope === "well"
                ? "No payments on any tract in this well yet"
                : "No payments for this scope yet"
            }
            editTrigger="button"
            editLayout="wide"
            height={420}
          />
        )}
      </Card>
    </Space>
  );
};

export default PaymentsTab;
